import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, EmptyState, DateInput } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftRight, Download } from "lucide-react";

const ALL = "__all__";

/** Umlagerungshistorie: eine Zeile = ein fachlicher Vorgang (Quelle -> Ziel), nicht zwei Bewegungen. */
export default function TransfersTab() {
  const { t, fmtDate } = useI18n();
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [flt, setFlt] = useState({ date_from: "", date_to: "", q: "", warehouse_id: "", storage_location_id: "", user_email: "" });

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(flt).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [flt]);

  const load = useCallback(() => {
    api.get(`/inventory/transfers?${params()}`).then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData({ rows: [] }); });
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/storage-locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const rows = data?.rows || [];

  const exportCsv = () => {
    const cols = [
      ["tr.title", (r) => r.id],
      ["common.date", (r) => `${fmtDate(r.created_at)} ${String(r.created_at || "").slice(11, 16)}`],
      ["inv.productSku", (r) => r.sku],
      ["common.name", (r) => r.product_name],
      ["ex.supplierLot", (r) => r.lot_number || ""],
      ["ex.bbd", (r) => (r.best_before_date ? fmtDate(r.best_before_date) : "")],
      ["common.quantity", (r) => r.quantity],
      ["ex.quality", (r) => t(`quality.${r.quality}`, r.quality)],
      ["ex.warehouse", (r) => r.warehouse_name || ""],
      ["tr.source", (r) => r.from_location_code],
      ["tr.target", (r) => r.to_location_code],
      ["tr.user", (r) => r.created_by_email || ""],
      ["tr.reason", (r) => r.reason || ""],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.map(([k]) => esc(t(k))).join(";"),
      ...rows.map((r) => cols.map(([, get]) => esc(get(r))).join(";"))].join("\r\n");
    const url = window.URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${t("tr.historyTitle")}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const set = (k) => (v) => setFlt({ ...flt, [k]: v === ALL ? "" : v });

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="transfers-tab">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-40">
          <label className="text-xs text-muted-foreground">{t("tr.dateFrom")}</label>
          <DateInput className="mt-1" value={flt.date_from} testId="transfers-filter-from"
            onChange={set("date_from")} />
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs text-muted-foreground">{t("tr.dateTo")}</label>
          <DateInput className="mt-1" value={flt.date_to} testId="transfers-filter-to"
            onChange={set("date_to")} />
        </div>
        <div className="w-full sm:w-44">
          <label className="text-xs text-muted-foreground">{t("inv.productSku")}</label>
          <Input className="mt-1 h-9" value={flt.q} data-testid="transfers-filter-search"
            onChange={(e) => set("q")(e.target.value)} />
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs text-muted-foreground">{t("ex.warehouse")}</label>
          <Select value={flt.warehouse_id || ALL} onValueChange={set("warehouse_id")}>
            <SelectTrigger className="mt-1 h-9" data-testid="transfers-filter-warehouse"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs text-muted-foreground">{t("ex.location")}</label>
          <Select value={flt.storage_location_id || ALL} onValueChange={set("storage_location_id")}>
            <SelectTrigger className="mt-1 h-9" data-testid="transfers-filter-location"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <label className="text-xs text-muted-foreground">{t("tr.user")}</label>
          <Select value={flt.user_email || ALL} onValueChange={set("user_email")}>
            <SelectTrigger className="mt-1 h-9" data-testid="transfers-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {users.map((u) => <SelectItem key={u.id || u.email} value={u.email}>{u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="h-9" onClick={exportCsv} disabled={!rows.length}
          data-testid="transfers-export-csv">
          <Download className="mr-1.5 h-4 w-4" /> {t("ex.exportCsv")}
        </Button>
      </div>

      {!data ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="transfers-empty" icon={ArrowLeftRight} title={t("tr.historyTitle")} hint={t("tr.noRows")} /> : (
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-muted/50"><tr>
                <Th sticky>{t("common.date")}</Th><Th sticky>{t("inv.productSku")}</Th>
                <Th sticky>{t("ex.supplierLot")}</Th><Th sticky>{t("ex.bbd")}</Th>
                <Th sticky right>{t("common.quantity")}</Th><Th sticky>{t("ex.quality")}</Th>
                <Th sticky>{t("tr.source")}</Th><Th sticky>{t("tr.target")}</Th>
                <Th sticky>{t("tr.user")}</Th><Th sticky>{t("tr.reason")}</Th>
              </tr></thead>
              <tbody data-testid="transfers-table">
                {rows.map((r, i) => (
                  <tr key={r.id} className="hover:bg-muted/40 transition-colors" data-testid={`transfer-row-${i}`}>
                    <Td>{fmtDate(r.created_at)} <span className="text-xs text-muted-foreground">{String(r.created_at || "").slice(11, 16)}</span></Td>
                    <Td><div className="font-medium text-foreground">{r.product_name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.sku}</div></Td>
                    <Td mono>{r.lot_number || "—"}</Td>
                    <Td>{r.best_before_date ? fmtDate(r.best_before_date) : "—"}</Td>
                    <Td right mono className="font-semibold">{r.quantity}</Td>
                    <Td>{t(`quality.${r.quality}`, r.quality)}</Td>
                    <Td mono>{r.from_location_code || "—"}</Td>
                    <Td mono>{r.to_location_code || "—"}</Td>
                    <Td className="text-xs">{r.created_by_email || "—"}</Td>
                    <Td className="text-muted-foreground">{r.reason || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
